import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Slider,
  FormControlLabel,
  Switch,
  Card,
  CardContent,
  Divider,
  Stack,
  Container,
  InputAdornment,
  Pagination,
  Radio,
  RadioGroup,
  FormLabel,
} from '@mui/material';
import MergeIcon from '@mui/icons-material/MergeType';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import SearchIcon from '@mui/icons-material/Search';
import { clientMergeAPI, DuplicatePair, MergeAnalysis, ClientSummary } from '../services/api';

/** Safely extract error message string from Axios error (avoids React error #31 from rendering objects) */
const getErrorMessage = (err: any, fallback: string): string => {
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e: any) => e.msg || String(e)).join(', ');
  return err.message || fallback;
};

interface DuplicateCardProps {
  pair: DuplicatePair;
  onMerge: (pair: DuplicatePair) => void;
}

const DuplicateCard: React.FC<DuplicateCardProps> = ({ pair, onMerge }) => {
  const getMatchTypeColor = (type: string) => {
    switch (type) {
      case 'exact':
        return 'error';
      case 'fuzzy':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'merge':
        return 'success';
      case 'review':
        return 'warning';
      case 'ignore':
        return 'default';
      default:
        return 'default';
    }
  };

  const isPrimary = (client: ClientSummary) => {
    return client.customer_id === pair.recommended_primary;
  };

  const renderClientCard = (client: ClientSummary, title: string) => (
    <Card variant="outlined" sx={{ height: '100%', bgcolor: isPrimary(client) ? 'success.50' : 'grey.50' }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">{title}</Typography>
          {isPrimary(client) && (
            <Chip label="Recommended Primary" color="success" size="small" />
          )}
        </Box>

        <Typography variant="body2" color="text.secondary">Customer ID</Typography>
        <Typography variant="body1" fontWeight="bold" mb={1}>{client.customer_id}</Typography>

        <Typography variant="body2" color="text.secondary">Business Name</Typography>
        <Typography variant="body1" mb={1}>{client.razon_social}</Typography>

        <Typography variant="body2" color="text.secondary">RUC</Typography>
        <Typography variant="body1" mb={1}>{client.ruc}</Typography>

        {client.source_company && (
          <>
            <Typography variant="body2" color="text.secondary">Source Company</Typography>
            <Typography variant="body1" mb={1}>
              <Chip
                label={client.source_company}
                size="small"
                color={client.source_company === 'BOTH' ? 'primary' : 'default'}
              />
            </Typography>
          </>
        )}

        {client.phone && (
          <>
            <Typography variant="body2" color="text.secondary">Phone</Typography>
            <Typography variant="body1" mb={1}>{client.phone}</Typography>
          </>
        )}

        {client.address && (
          <>
            <Typography variant="body2" color="text.secondary">Address</Typography>
            <Typography variant="body1" mb={1}>{client.address}</Typography>
          </>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="body2" color="text.secondary" mb={1}>Related Data</Typography>
        <Stack direction="row" spacing={2} flexWrap="wrap">
          <Typography variant="caption">Contracts: {client.contracts_count}</Typography>
          <Typography variant="caption">Equipment: {client.equipment_count}</Typography>
          <Typography variant="caption">Invoices: {client.invoices_count}</Typography>
          <Typography variant="caption">AR: {client.ar_count}</Typography>
        </Stack>

        <Typography variant="body2" fontWeight="bold" mt={1}>
          Total Records: {client.contracts_count + client.equipment_count + client.invoices_count + client.ar_count}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" gap={1} alignItems="center">
          <Chip
            label={`${(pair.similarity * 100).toFixed(1)}% Match`}
            color={pair.similarity > 0.9 ? 'error' : 'warning'}
            size="small"
          />
          <Chip
            label={pair.match_type.toUpperCase()}
            color={getMatchTypeColor(pair.match_type)}
            size="small"
          />
          <Chip
            label={pair.recommended_action.toUpperCase()}
            color={getActionColor(pair.recommended_action)}
            size="small"
          />
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<MergeIcon />}
          onClick={() => onMerge(pair)}
        >
          Review Merge
        </Button>
      </Box>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <Box flex={1}>
          {renderClientCard(pair.client1, 'Client 1')}
        </Box>
        <Box flex={1}>
          {renderClientCard(pair.client2, 'Client 2')}
        </Box>
      </Stack>
    </Paper>
  );
};

const ClientMerge: React.FC = () => {
  const [duplicates, setDuplicates] = useState<DuplicatePair[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [totalDuplicates, setTotalDuplicates] = useState(0);

  // Filter states
  const [includeExact, setIncludeExact] = useState(true);
  const [includeFuzzy, setIncludeFuzzy] = useState(true);
  const [minSimilarity, setMinSimilarity] = useState(0.7);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Pagination states
  const [page, setPage] = useState(1);
  const itemsPerPage = 20;

  // Filtered duplicates based on search
  const filteredDuplicates = useMemo(() => {
    if (!searchQuery.trim()) return duplicates;
    const q = searchQuery.trim().toLowerCase();
    return duplicates.filter(
      (pair) =>
        pair.client1.razon_social.toLowerCase().includes(q) ||
        pair.client2.razon_social.toLowerCase().includes(q) ||
        pair.client1.customer_id.toLowerCase().includes(q) ||
        pair.client2.customer_id.toLowerCase().includes(q) ||
        pair.client1.ruc.toLowerCase().includes(q) ||
        pair.client2.ruc.toLowerCase().includes(q)
    );
  }, [duplicates, searchQuery]);

  // Merge dialog states
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [selectedPair, setSelectedPair] = useState<DuplicatePair | null>(null);
  const [analysis, setAnalysis] = useState<MergeAnalysis | null>(null);
  const [analyzingMerge, setAnalyzingMerge] = useState(false);
  const [mergingClients, setMergingClients] = useState(false);
  const [mergeNotes, setMergeNotes] = useState('');
  const [mergeStrategy, setMergeStrategy] = useState<'primary_only' | 'merge_attributes' | 'keep_complete'>('merge_attributes');
  const [chosenRuc, setChosenRuc] = useState<string>('');

  // Manual merge states
  const [manualPrimaryId, setManualPrimaryId] = useState('');
  const [manualSecondaryId, setManualSecondaryId] = useState('');

  const handleManualMerge = () => {
    const primary = manualPrimaryId.trim();
    const secondary = manualSecondaryId.trim();
    if (!primary || !secondary) return;
    if (primary === secondary) {
      setError('Primary and secondary client IDs must be different');
      return;
    }
    // Build a minimal pair to open the merge dialog with the standard flow
    const fakePair: DuplicatePair = {
      client1: { customer_id: primary, razon_social: '', ruc: '', is_active: true, contracts_count: 0, equipment_count: 0, invoices_count: 0, ar_count: 0 },
      client2: { customer_id: secondary, razon_social: '', ruc: '', is_active: true, contracts_count: 0, equipment_count: 0, invoices_count: 0, ar_count: 0 },
      similarity: 0,
      match_type: 'exact',
      recommended_primary: primary,
      recommended_action: 'review',
    };
    handleOpenMergeDialog(fakePair);
  };

  const loadDuplicates = async () => {
    console.log('Loading duplicates...');
    setLoading(true);
    setError(null);
    try {
      const response = await clientMergeAPI.getDuplicates({
        include_exact: includeExact,
        include_fuzzy: includeFuzzy,
        min_similarity: minSimilarity,
        limit: 500, // Get more results to properly paginate
      });
      console.log('Duplicates loaded:', response.total_duplicates);
      setTotalDuplicates(response.total_duplicates);
      setDuplicates(response.duplicates || []);
    } catch (err: any) {
      console.error('Error loading duplicates:', err);
      setError(getErrorMessage(err, 'Failed to load duplicates'));
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = () => {
    setPage(1); // Reset to first page when filters change
    loadDuplicates();
  };

  useEffect(() => {
    loadDuplicates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenMergeDialog = async (pair: DuplicatePair) => {
    console.log('Opening merge dialog for pair:', pair.client1.customer_id, 'and', pair.client2.customer_id);

    // Reset all state first
    setError(null);
    setMergingClients(false);
    setAnalyzingMerge(false);
    setMergeNotes('');
    setMergeStrategy('merge_attributes');
    setChosenRuc('');

    setSelectedPair(pair);
    setMergeDialogOpen(true);
    setAnalyzingMerge(true);

    try {
      const primaryId = pair.recommended_primary;
      const secondaryId = pair.recommended_primary === pair.client1.customer_id ? pair.client2.customer_id : pair.client1.customer_id;

      console.log('Analyzing merge:', primaryId, '(primary) <-', secondaryId, '(secondary)');
      const mergeAnalysis = await clientMergeAPI.analyzeMerge(primaryId, secondaryId);
      console.log('Analysis complete:', mergeAnalysis);
      setAnalysis(mergeAnalysis);

      // Set default RUC to primary client's RUC
      setChosenRuc(mergeAnalysis.primary_client.ruc);
    } catch (err: any) {
      console.error('Analysis error:', err);
      setError(getErrorMessage(err, 'Failed to analyze merge'));
    } finally {
      setAnalyzingMerge(false);
    }
  };

  const handleCloseMergeDialog = () => {
    console.log('Closing merge dialog and resetting state');
    setMergeDialogOpen(false);
    setSelectedPair(null);
    setAnalysis(null);
    setMergeNotes('');
    setMergeStrategy('merge_attributes'); // Reset to default
    setChosenRuc(''); // Reset chosen RUC
    setError(null);
    setMergingClients(false); // Ensure loading state is reset
    setAnalyzingMerge(false); // Ensure analyzing state is reset
  };

  const handleExecuteMerge = async () => {
    if (!selectedPair || !analysis) {
      console.log('Cannot execute merge: missing selectedPair or analysis');
      return;
    }

    // Prevent double-click
    if (mergingClients) {
      console.log('Merge already in progress, ignoring click');
      return;
    }

    console.log('Starting merge execution...');
    console.log('Primary:', analysis.primary_client.customer_id);
    console.log('Secondary:', analysis.secondary_client.customer_id);
    console.log('Strategy:', mergeStrategy);

    setMergingClients(true);
    setError(null);

    try {
      console.log('Calling executeMerge API...');
      const result = await clientMergeAPI.executeMerge({
        primary_client_id: analysis.primary_client.customer_id,
        secondary_client_id: analysis.secondary_client.customer_id,
        merge_strategy: mergeStrategy,
        chosen_ruc: chosenRuc || undefined,
        notes: mergeNotes,
      });

      console.log('Merge result:', result);

      if (result.success) {
        console.log('Merge successful!');
        setSuccess(`Successfully merged ${analysis.secondary_client.customer_id} into ${analysis.primary_client.customer_id}`);
        handleCloseMergeDialog();
        setPage(1); // Reset to first page after merge

        // Reload duplicates in the background without awaiting
        console.log('Triggering duplicates list reload...');
        loadDuplicates().then(() => {
          console.log('Duplicates list reloaded successfully');
        }).catch((err) => {
          console.error('Error reloading duplicates:', err);
        });
      } else {
        console.error('Merge failed:', result.message);
        setError(result.message);
      }
    } catch (err: any) {
      console.error('Merge error:', err);
      console.error('Error response:', err.response);
      setError(getErrorMessage(err, 'Failed to execute merge'));
    } finally {
      console.log('Merge execution completed, resetting loading state');
      setMergingClients(false);
    }
  };

  return (
    

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Client Merge Utility
        </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <TextField
          size="small"
          placeholder="Search by client name, ID, or RUC..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(1);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2, width: { xs: '100%', md: 400 } }}
        />
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" flexWrap="wrap">
          <FormControlLabel
            control={
              <Switch
                checked={includeExact}
                onChange={(e) => setIncludeExact(e.target.checked)}
              />
            }
            label="Exact Duplicates"
          />
          <FormControlLabel
            control={
              <Switch
                checked={includeFuzzy}
                onChange={(e) => setIncludeFuzzy(e.target.checked)}
              />
            }
            label="Fuzzy Duplicates"
          />
          <Box flex={1} minWidth={200}>
            <Typography gutterBottom>Min Similarity: {(minSimilarity * 100).toFixed(0)}%</Typography>
            <Slider
              value={minSimilarity}
              onChange={(_, value) => setMinSimilarity(value as number)}
              min={0.5}
              max={1.0}
              step={0.05}
              marks
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${(value * 100).toFixed(0)}%`}
            />
          </Box>
          <Button
            variant="contained"
            onClick={handleFilterChange}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Apply Filters'}
          </Button>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Manual Merge
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Merge two clients by entering their Customer IDs directly. Use this when the duplicate detector doesn't find them automatically.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
          <TextField
            size="small"
            label="Primary ID (keep)"
            placeholder="e.g. C0540"
            value={manualPrimaryId}
            onChange={(e) => setManualPrimaryId(e.target.value)}
            sx={{ width: 200 }}
          />
          <TextField
            size="small"
            label="Secondary ID (merge into primary)"
            placeholder="e.g. F24-1500"
            value={manualSecondaryId}
            onChange={(e) => setManualSecondaryId(e.target.value)}
            sx={{ width: 200 }}
          />
          <Button
            variant="contained"
            startIcon={<MergeIcon />}
            onClick={handleManualMerge}
            disabled={!manualPrimaryId.trim() || !manualSecondaryId.trim()}
          >
            Analyze Merge
          </Button>
        </Stack>
      </Paper>

      {loading && (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      )}

      {!loading && duplicates.length === 0 && (
        <Alert severity="info">
          No duplicate clients found with the current filters.
        </Alert>
      )}

      {!loading && duplicates.length > 0 && filteredDuplicates.length === 0 && (
        <Alert severity="info">
          No duplicates match "{searchQuery}". Try a different search term.
        </Alert>
      )}

      {!loading && filteredDuplicates.length > 0 && (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              {searchQuery.trim()
                ? `${filteredDuplicates.length} match${filteredDuplicates.length !== 1 ? 'es' : ''} of ${totalDuplicates} duplicate pairs`
                : `Found ${totalDuplicates} Duplicate Pair${totalDuplicates !== 1 ? 's' : ''}`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Showing {((page - 1) * itemsPerPage) + 1}-{Math.min(page * itemsPerPage, filteredDuplicates.length)} of {filteredDuplicates.length}
            </Typography>
          </Box>

          {filteredDuplicates.length > itemsPerPage && (
            <Box display="flex" justifyContent="center" mb={3}>
              <Pagination
                count={Math.ceil(filteredDuplicates.length / itemsPerPage)}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
                size="large"
              />
            </Box>
          )}

          {filteredDuplicates
            .slice((page - 1) * itemsPerPage, page * itemsPerPage)
            .map((pair, index) => (
              <DuplicateCard
                key={`${pair.client1.customer_id}-${pair.client2.customer_id}-${index}`}
                pair={pair}
                onMerge={handleOpenMergeDialog}
              />
            ))}

          {filteredDuplicates.length > itemsPerPage && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={Math.ceil(filteredDuplicates.length / itemsPerPage)}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </>
      )}

      {/* Merge Analysis Dialog */}
      <Dialog
        open={mergeDialogOpen}
        onClose={handleCloseMergeDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <MergeIcon />
            <Typography variant="h6">Merge Analysis</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {mergingClients && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Box display="flex" alignItems="center" gap={2}>
                <CircularProgress size={20} />
                <Typography>Merging clients... Please wait, this may take a few seconds.</Typography>
              </Box>
            </Alert>
          )}

          {analyzingMerge && (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          )}

          {!analyzingMerge && analysis && (
            <Box>
              <Alert severity={analysis.is_safe ? 'info' : 'error'} sx={{ mb: 2 }}>
                {analysis.is_safe ? (
                  <>
                    <CheckCircleIcon sx={{ mr: 1 }} />
                    This merge is safe to execute
                  </>
                ) : (
                  <>
                    <WarningIcon sx={{ mr: 1 }} />
                    This merge has conflicts that need resolution
                  </>
                )}
              </Alert>

              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Primary Client (will be kept):
              </Typography>
              <Typography variant="body2" mb={2}>
                {analysis.primary_client.customer_id} - {analysis.primary_client.razon_social}
              </Typography>

              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Secondary Client (will be merged and deleted):
              </Typography>
              <Typography variant="body2" mb={3}>
                {analysis.secondary_client.customer_id} - {analysis.secondary_client.razon_social}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Data to Migrate:
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Table</TableCell>
                      <TableCell align="right">Records</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(analysis.data_to_migrate).map(([table, count]) => (
                      count > 0 && (
                        <TableRow key={table}>
                          <TableCell>{table.replace(/_/g, ' ').toUpperCase()}</TableCell>
                          <TableCell align="right">{count}</TableCell>
                        </TableRow>
                      )
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {analysis.conflicts.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" gutterBottom fontWeight="bold" color="warning.main">
                    <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Conflicts ({analysis.conflicts.length}):
                  </Typography>
                  {analysis.conflicts.map((conflict, idx) => (
                    <Alert severity="warning" key={idx} sx={{ mb: 1 }}>
                      <Typography variant="body2" fontWeight="bold">{conflict.type}</Typography>
                      <Typography variant="caption">
                        Primary: {JSON.stringify(conflict.primary_value)} |
                        Secondary: {JSON.stringify(conflict.secondary_value)}
                      </Typography>
                      <Typography variant="caption" display="block">
                        Resolution: {conflict.resolution} → {JSON.stringify(conflict.kept_value)}
                      </Typography>
                    </Alert>
                  ))}
                </>
              )}

              {analysis.warnings.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" gutterBottom fontWeight="bold" color="info.main">
                    <InfoIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Warnings ({analysis.warnings.length}):
                  </Typography>
                  {analysis.warnings.map((warning, idx) => (
                    <Alert severity="info" key={idx} sx={{ mb: 1 }}>
                      {warning}
                    </Alert>
                  ))}
                </>
              )}

              {/* RUC Selection if clients have different RUCs */}
              {analysis.conflicts.some((c) => c.type === 'different_ruc' && c.requires_user_decision) && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <FormControl component="fieldset" sx={{ mb: 2 }}>
                    <FormLabel component="legend" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                      <WarningIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
                      Choose RUC for Merged Client *
                    </FormLabel>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      Contracts will maintain their original RUC associations
                    </Typography>
                    <RadioGroup
                      value={chosenRuc}
                      onChange={(e) => setChosenRuc(e.target.value)}
                    >
                      <FormControlLabel
                        value={analysis.primary_client.ruc}
                        control={<Radio />}
                        label={
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {analysis.primary_client.ruc}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Primary client - {analysis.primary_client.razon_social}
                            </Typography>
                          </Box>
                        }
                      />
                      <FormControlLabel
                        value={analysis.secondary_client.ruc}
                        control={<Radio />}
                        label={
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {analysis.secondary_client.ruc}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Secondary client - {analysis.secondary_client.razon_social}
                            </Typography>
                          </Box>
                        }
                      />
                    </RadioGroup>
                  </FormControl>
                </>
              )}

              <Divider sx={{ my: 2 }} />

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Merge Strategy</InputLabel>
                <Select
                  value={mergeStrategy}
                  label="Merge Strategy"
                  onChange={(e) => setMergeStrategy(e.target.value as any)}
                >
                  <MenuItem value="primary_only">Primary Only - Keep only primary client data</MenuItem>
                  <MenuItem value="merge_attributes">Merge Attributes - Fill in blanks from secondary</MenuItem>
                  <MenuItem value="keep_complete">Keep Complete - Keep most complete record</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes (optional)"
                value={mergeNotes}
                onChange={(e) => setMergeNotes(e.target.value)}
                placeholder="Add any notes about this merge..."
              />

              <Typography variant="caption" color="text.secondary" display="block" mt={2}>
                Estimated duration: {analysis.estimated_duration_seconds.toFixed(1)} seconds
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMergeDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleExecuteMerge}
            disabled={!analysis || !analysis.is_safe || mergingClients}
            startIcon={mergingClients ? <CircularProgress size={20} /> : <MergeIcon />}
          >
            {mergingClients ? 'Merging...' : 'Execute Merge'}
          </Button>
        </DialogActions>
      </Dialog>
      </Container>
  );

};

export default ClientMerge;
